import React from 'react';
import { Container, Row, Col, Tabs, Tab, Form, InputGroup, Card, Badge } from 'react-bootstrap';
import { MsalAuthenticationTemplate, useMsal } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRefresh, faSearch, faTableList } from '@fortawesome/free-solid-svg-icons';

import { loginRequest } from '../../authConfig';
import { PageLayout2 } from '../../Components/PageLayout/PageLayout2';
import ServerTable from '../../Components/Table/ServerTabe';
import { ConciliationReportsApi } from '../../Services/ConciliationApiDataService';
import { getStoredConciliationCompanyId, setStoredConciliationCompanyId } from './companySelectionStore';
import './ConciliationReportsPage.scss'
import { formatCurrencyNum, formatDate, formatNumber } from '../../Utils/formaters';
import { Permissions } from '../../Security/permissions';
import { useSecurity } from '../../Contexts/SecurityContext';

const useToken = () => {
  const { instance, accounts } = useMsal();
  return React.useCallback(async () => {
    const req = { ...loginRequest, account: accounts[0] };
    const { accessToken } = await instance.acquireTokenSilent(req);
    return accessToken;
  }, [instance, accounts]);
};

const statusBadge = (status) => {
  const s = (status ?? '').toLowerCase();
  if (s === 'match') return 'success';
  if (s === 'oficialnomatch') return 'warning';
  if (s === 'entfinnomatch') return 'warning';
  if (s === 'erpnomatch') return 'secondary';
  return 'dark';
};

const valueFrom = (row, keys = []) => {
  for (const key of keys) {
    const value = row?.[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return '';
};

const ConciliagastosTab = ({ companyId, getToken }) => {
  const [refreshTick, setRefreshTick] = React.useState(0);
  const [search, setSearch] = React.useState('');
  const [status, setStatus] = React.useState('');
  const [from, setFrom] = React.useState('');
  const [to, setTo] = React.useState('');

  const statusOptions = [
    { value: '', label: 'All statuses' },
    { value: 'MATCH', label: 'MATCH' },
    { value: 'OFICIALNOMATCH', label: 'OFICIALNOMATCH' },
    { value: 'ERPNOMATCH', label: 'ERPNOMATCH' }
  ];

  const fetchData = React.useCallback(async ({ pageIndex, pageSize, globalFilter }) => {
    void refreshTick;
    if (!companyId) return { items: [], totalItems: 0 };

    const token = await getToken();
    const res = await ConciliationReportsApi.getConciliationGastosReport({
      companyId,
      page: pageIndex + 1,
      pageSize,
      search: globalFilter || search || '',
      status: status || undefined,
      from: from || undefined,
      to: to || undefined,
      sortBy: 'date',
      sortDir: 'desc'
    }, token);

    return {
      items: res.items ?? [],
      totalItems: res.totalCount ?? 0
    };
  }, [companyId, getToken, search, status, from, to, refreshTick]);

  const columns = React.useMemo(() => [
    {
      header: 'Status',
      accessorKey: 'estadoConcilia',
      cell: ({ row }) => <Badge bg={statusBadge(row.original.estadoConcilia)}>{row.original.estadoConcilia}</Badge>
    },
    { header: 'Priority', accessorKey: 'prioridadConcilia' },
    { header: 'Company', accessorKey: 'goEmpresaID' },
    { header: 'Date', accessorKey: 'goFecha', cell: info => formatDate(info.getValue()) },
    { header: 'Auth Number', accessorKey: 'goNumeroAutorizacion' },
    { header: 'SAT Serie', accessorKey: 'satSerie' },
    { header: 'SAT DTE', accessorKey: 'satNumeroDte' },
    { header: 'SAT Vendor', accessorKey: 'satNombreEmisor' },
    { header: 'SAT NIT', accessorKey: 'satNitEmisor' },
    { header: 'SAT Total', accessorKey: 'satGranTotal', cell: info => formatCurrencyNum(info.getValue()) },
    { header: 'SAT Currency', accessorKey: 'satMoneda' },
    { header: 'ERP DocEntry', accessorKey: 'sapDocEntry' },
    { header: 'ERP DocNum', accessorKey: 'sapDocNum' },
    { header: 'ERP Date', accessorKey: 'sapDocDate', cell: info => formatDate(info.getValue()) },
    { header: 'ERP CardCode', accessorKey: 'sapCardCode' },
    { header: 'ERP CardName', accessorKey: 'sapCardName' },
    { header: 'ERP Total', accessorKey: 'sapDocTotal', cell: info => formatCurrencyNum(info.getValue()) },
    { header: 'ERP Currency', accessorKey: 'sapCurrency' },
    { header: 'GE Number', accessorKey: 'geNumeroID' },
    { header: 'GO Number', accessorKey: 'goNumeroID' },
    // { header: 'Conciliation Key', accessorKey: 'conciliationKey' }
  ], []);

  return (
    <div className="conciliation-report-tab-shell">
      <Card className="h-100 shadow-sm">
        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div className="fw-bold">
            <FontAwesomeIcon icon={faTableList} className="me-2" />
            Conciliagastos Report
          </div>

          <div className="d-flex flex-wrap gap-2 align-items-center">
            <InputGroup size="sm" style={{ width: 260 }}>
              <InputGroup.Text>
                <FontAwesomeIcon icon={faSearch} />
              </InputGroup.Text>
              <Form.Control
                placeholder="Search vendor, auth, SAP doc..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </InputGroup>

            <div style={{ width: 210 }}>
              <Select
                classNamePrefix="react-select"
                options={statusOptions}
                value={statusOptions.find(x => x.value === status)}
                onChange={(opt) => setStatus(opt?.value ?? '')}
              />
            </div>

            <Form.Control
              size="sm"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              style={{ width: 150 }}
            />

            <Form.Control
              size="sm"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              style={{ width: 150 }}
            />

            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => setRefreshTick(t => t + 1)}
            >
              <FontAwesomeIcon icon={faRefresh} />
            </button>
          </div>
        </Card.Header>

        <Card.Body className="conciliation-report-card-body p-0">
          <div className="conciliation-report-scroll">
            <ServerTable
              columns={columns}
              fetchData={fetchData}
              tableClass="table-hover table-align-right"
              containerClass="table-responsive"
              headerClass="table-light"
              enableGlobalFilter={false}
              initialPageSize={25}
            />
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

const ConciliapagosTab = ({ companyId, getToken }) => {
  const [refreshTick, setRefreshTick] = React.useState(0);
  const [search, setSearch] = React.useState('');
  const [status, setStatus] = React.useState('');
  const [from, setFrom] = React.useState('');
  const [to, setTo] = React.useState('');

  const statusOptions = [
    { value: '', label: 'All statuses' },
    { value: 'MATCH', label: 'MATCH' },
    { value: 'ENTFINNOMATCH', label: 'ENTFINNOMATCH' },
    { value: 'ERPNOMATCH', label: 'ERPNOMATCH' }
  ];

  const fetchData = React.useCallback(async ({ pageIndex, pageSize, globalFilter }) => {
    void refreshTick;
    if (!companyId) return { items: [], totalItems: 0 };

    const token = await getToken();
    const res = await ConciliationReportsApi.getConciliationPagosReport({
      companyId,
      page: pageIndex + 1,
      pageSize,
      search: globalFilter || search || '',
      status: status || undefined,
      from: from || undefined,
      to: to || undefined,
      sortBy: 'date',
      sortDir: 'desc'
    }, token);

    return {
      items: res.items ?? [],
      totalItems: res.totalCount ?? res.totalItems ?? 0
    };
  }, [companyId, getToken, search, status, from, to, refreshTick]);

  const columns = React.useMemo(() => [
    {
      header: 'Status',
      id: 'estadoConcilia',
      cell: ({ row }) => {
        const statusValue = valueFrom(row.original, ['estadoConcilia', 'EstadoConcilia', 'status']);
        return <Badge bg={statusBadge(statusValue)}>{statusValue}</Badge>;
      }
    },
    { header: 'Priority', id: 'prioridadConcilia', cell: ({ row }) => valueFrom(row.original, ['prioridadConcilia', 'PrioridadConcilia']) },

    { header: 'Conciliation Key', id: 'conciliationKey', cell: ({ row }) => valueFrom(row.original, ['conciliationKey', 'ConciliationKey']) },

    { header: 'Mov Statement Id', id: 'movBankStatementId', cell: ({ row }) => valueFrom(row.original, ['movBankStatementId', 'MovBankStatementId']) },
    { header: 'Mov Entry Id', id: 'movBankEntryId', cell: ({ row }) => valueFrom(row.original, ['movBankEntryId', 'MovBankEntryId']) },
    { header: 'Mov Booking Date', id: 'movBookingDate', cell: info => formatDate(info.getValue()) },
    { header: 'Mov Value Date', id: 'movValueDate', cell: info => formatDate(info.getValue()) },
    { header: 'Mov Account', id: 'movAccountNumber', cell: ({ row }) => valueFrom(row.original, ['movAccountNumber', 'MovAccountNumber']) },
    { header: 'Mov Currency', id: 'movCurrencyEntry', cell: ({ row }) => valueFrom(row.original, ['movCurrencyEntry', 'MovCurrencyEntry']) },
    { header: 'Mov Amount', id: 'movAmount', cell: info => formatCurrencyNum(info.getValue()) },
    { header: 'Mov D/C', id: 'movDc', cell: ({ row }) => valueFrom(row.original, ['movDebitCreditIndicator', 'MovDebitCreditIndicator']) },
    { header: 'Mov Reference', id: 'movEntryReference', cell: ({ row }) => valueFrom(row.original, ['movEntryReference', 'MovEntryReference']) },
    { header: 'Mov Additional Info', id: 'movAdditionalInfo', cell: ({ row }) => valueFrom(row.original, ['movAdditionalInfo', 'MovAdditionalInfo']) },
    { header: 'Mov Company', id: 'movCompanyName', cell: ({ row }) => valueFrom(row.original, ['movCompanyName', 'MovCompanyName']) },

    { header: 'SAP DocEntry', id: 'sapDocEntry', cell: ({ row }) => valueFrom(row.original, ['sapDocEntry', 'SapDocEntry']) },
    { header: 'SAP Company Id', id: 'sapCompanyId', cell: ({ row }) => valueFrom(row.original, ['sapCompanyId', 'SapCompanyId']) },
    { header: 'SAP DocNum', id: 'sapDocNum', cell: ({ row }) => valueFrom(row.original, ['sapDocNum', 'SapDocNum']) },
    { header: 'SAP DocDate', id: 'sapDocDate', cell: info => formatDate(info.getValue()) },
    { header: 'SAP Currency', id: 'sapDocCurrency', cell: ({ row }) => valueFrom(row.original, ['sapDocCurrency', 'SapDocCurrency']) },
    { header: 'SAP CardCode', id: 'sapCardCode', cell: ({ row }) => valueFrom(row.original, ['sapCardCode', 'SapCardCode']) },
    { header: 'SAP CardName', id: 'sapCardName', cell: ({ row }) => valueFrom(row.original, ['sapCardName', 'SapCardName']) },
    { header: 'SAP Transfer Account', id: 'sapTransferAccount', cell: ({ row }) => valueFrom(row.original, ['sapTransferAccount', 'SapTransferAccount']) },
    { header: 'SAP Transfer Sum', id: 'sapTransferSum', cell: info => formatCurrencyNum(info.getValue()) },
    { header: 'SAP Transfer Date', id: 'sapTransferDate', cell: info => formatDate(info.getValue()) },
    { header: 'SAP Transfer Ref', id: 'sapTransferReference', cell: ({ row }) => valueFrom(row.original, ['sapTransferReference', 'SapTransferReference']) },
    { header: 'SAP Counter Ref', id: 'sapCounterReference', cell: ({ row }) => valueFrom(row.original, ['sapCounterReference', 'SapCounterReference']) },
    { header: 'SAP Remarks', id: 'sapRemarks', cell: ({ row }) => valueFrom(row.original, ['sapRemarks', 'SapRemarks']) },
    { header: 'SAP Journal Remarks', id: 'sapJournalRemarks', cell: ({ row }) => valueFrom(row.original, ['sapJournalRemarks', 'SapJournalRemarks']) },

    { header: 'PE Empresa', id: 'peEmpresaId', cell: ({ row }) => valueFrom(row.original, ['peEmpresaID', 'PEEmpresaID']) },
    { header: 'PE Entidad', id: 'peEntidadId', cell: ({ row }) => valueFrom(row.original, ['pentidadFinancieraID', 'PEEntidadFinancieraID']) },
    { header: 'PE Cuenta', id: 'peCuentaId', cell: ({ row }) => valueFrom(row.original, ['peCuentaID', 'PECuentaID']) },
    { header: 'PE N. Trans', id: 'peNumeroTx', cell: ({ row }) => valueFrom(row.original, ['peNumeroTransaccion', 'PENumeroTransaccion']) },
    { header: 'PE Fecha', id: 'peFecha', cell: info => formatDate(info.getValue()) },
    { header: 'PE Monto', id: 'peMonto', cell: info => formatCurrencyNum(info.getValue()) },

    { header: 'PF Empresa', id: 'pfEmpresaId', cell: ({ row }) => valueFrom(row.original, ['pfEmpresaID', 'PFEmpresaID']) },
    { header: 'PF Entidad', id: 'pfEntidadId', cell: ({ row }) => valueFrom(row.original, ['pfEntidadFinancieraID', 'PFEntidadFinancieraID']) },
    { header: 'PF Cuenta', id: 'pfCuentaId', cell: ({ row }) => valueFrom(row.original, ['pfCuentaID', 'PFCuentaID']) },
    { header: 'PF N. Trans', id: 'pfNumeroTx', cell: ({ row }) => valueFrom(row.original, ['pfNumeroTransaccion', 'PFNumeroTransaccion']) },
    { header: 'PF Fecha', id: 'pfFecha', cell: info => formatDate(info.getValue()) },
    { header: 'PF Monto', id: 'pfMonto', cell: info => formatCurrencyNum(info.getValue()) },
    { header: 'PF Llave', id: 'pfLlave', cell: ({ row }) => valueFrom(row.original, ['pFllave', 'PFllave']) },
  ], []);

  return (
    <div className="conciliation-report-tab-shell">
      <Card className="h-100 shadow-sm">
        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div className="fw-bold">
            <FontAwesomeIcon icon={faTableList} className="me-2" />
            Conciliapagos Report
          </div>

          <div className="d-flex flex-wrap gap-2 align-items-center">
            <InputGroup size="sm" style={{ width: 260 }}>
              <InputGroup.Text>
                <FontAwesomeIcon icon={faSearch} />
              </InputGroup.Text>
              <Form.Control
                placeholder="Search bank ref, amount, SAP payment..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </InputGroup>

            <div style={{ width: 210 }}>
              <Select
                classNamePrefix="react-select"
                options={statusOptions}
                value={statusOptions.find((x) => x.value === status)}
                onChange={(opt) => setStatus(opt?.value ?? '')}
              />
            </div>

            <Form.Control
              size="sm"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              style={{ width: 150 }}
            />

            <Form.Control
              size="sm"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              style={{ width: 150 }}
            />

            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => setRefreshTick((t) => t + 1)}
            >
              <FontAwesomeIcon icon={faRefresh} />
            </button>
          </div>
        </Card.Header>

        <Card.Body className="conciliation-report-card-body p-0">
          <div className="conciliation-report-scroll">
            <ServerTable
              columns={columns}
              fetchData={fetchData}
              tableClass="table-hover table-align-right"
              containerClass="table-responsive"
              headerClass="table-light"
              enableGlobalFilter={false}
              initialPageSize={25}
            />
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

const ConciliationReportsPageContent = () => {
  const getToken = useToken();
  const { companiesFor, hasCompanyPermission } = useSecurity();

  const [companyId, setCompanyId] = React.useState(() => getStoredConciliationCompanyId());
  const [companyOptions, setCompanyOptions] = React.useState([]);
  const [loadingCompanies, setLoadingCompanies] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState('gastos');

  React.useEffect(() => {
    const opts = companiesFor(Permissions.Conciliation.Reports).map(company => ({
      value: company.companyId,
      label: company.companyName || company.companyDb || `Company ${company.companyId}`
    }));
    setCompanyOptions(opts);
    setLoadingCompanies(false);
    if (companyId && !opts.some(option => option.value === companyId)) {
      setCompanyId(null);
      setStoredConciliationCompanyId(null);
    }
  }, [companiesFor, companyId]);

  const selectedCompany = React.useMemo(
    () => companyOptions.find(o => o.value === companyId) ?? null,
    [companyOptions, companyId]
  );
  const authorizedCompanyId = companyId && hasCompanyPermission(companyId, Permissions.Conciliation.Reports)
    ? companyId
    : null;

  return (
    <Container fluid className="conciliation-reports-page h-100 d-flex flex-column">
      <div className="d-flex justify-content-between align-items-center w-100 mb-2">
        <div style={{ minWidth: 280 }}>
          <Select
            classNamePrefix="react-select"
            options={[{ value: null, label: 'Select company...' }, ...companyOptions]}
            value={selectedCompany}
            onChange={opt => {
              const id = opt?.value ?? null;
              setCompanyId(id);
              setStoredConciliationCompanyId(id);
            }}
            isLoading={loadingCompanies}
            placeholder="Select company…"
            menuPortalTarget={document.body}
            styles={{ menuPortal: b => ({ ...b, zIndex: 9999 }) }}
          />
        </div>
      </div>
      {!authorizedCompanyId && (
        <div className="alert alert-info mb-2">
          Select a company to load conciliation report records.
        </div>
      )}

      <div className="conciliation-reports-tabs-wrap flex-grow-1 d-flex flex-column min-h-0">
        <Tabs
          activeKey={activeTab}
          onSelect={k => setActiveTab(k)}
          className="conciliation-reports-tabs mb-0 "
        >
          <Tab eventKey="gastos" title="Conciliación Gastos" className="h-100">
            <ConciliagastosTab companyId={authorizedCompanyId} getToken={getToken} />
          </Tab>

          <Tab eventKey="pagos" title="Conciliación Pagos" className="h-100">
            <ConciliapagosTab companyId={authorizedCompanyId} getToken={getToken} />
          </Tab>
        </Tabs>
      </div>
    </Container>
  );
};

const ConciliationReportsPage = () => (
  <MsalAuthenticationTemplate interactionType={InteractionType.Redirect} authenticationRequest={loginRequest}>
    <PageLayout2 title="Conciliation Reports">
      <ConciliationReportsPageContent />
    </PageLayout2>
  </MsalAuthenticationTemplate>
);

export default ConciliationReportsPage;
